import { useEffect, useState } from 'react';

import { Link, useLocation } from 'react-router-dom';

import { styled, useTheme } from '@mui/material/styles';
import { Box, Fade, Grid, Skeleton, Typography } from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MainCard from 'ui-component/cards/MainCard';
import { useDispatch, useSelector } from 'react-redux';
import { getSecondLevelFacets } from 'store/slices/healthContent';
import { DefaultRootStateProps } from 'types';
import CardRatings from './CardRatings';
import TableProducts from '../TableProducts';
import { useIntl } from 'react-intl';
import { ResumenProducts } from 'types/components/shared';
import DownloadReportsHealth from '../DownloadReportsHealth';

const CardWrapper = styled(MainCard)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.dark : theme.palette.secondary.dark,
    color: '#fff',
    overflow: 'hidden',
    position: 'relative',
    '&:after': {
        content: '""',
        position: 'absolute',
        width: 210,
        height: 210,
        background:
            theme.palette.mode === 'dark'
                ? `linear-gradient(210.04deg, ${theme.palette.secondary.dark} -50.94%, rgba(144, 202, 249, 0) 95.49%)`
                : theme.palette.secondary[800],
        borderRadius: '50%',
        top: -85,
        right: -95,
        [theme.breakpoints.down('sm')]: {
            top: -105,
            right: -140
        }
    },
    '&:before': {
        content: '""',
        position: 'absolute',
        width: 210,
        height: 210,
        background:
            theme.palette.mode === 'dark'
                ? `linear-gradient(140.9deg, ${theme.palette.secondary.dark} -14.02%, rgba(144, 202, 249, 0) 85.50%)`
                : theme.palette.secondary[800],
        borderRadius: '50%',
        top: -125,
        right: -15,
        opacity: 0.5,
        [theme.breakpoints.down('sm')]: {
            top: -155,
            right: -70
        }
    }
}));

const Facets = () => {
    const location = useLocation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const [secondLevel, setSecondLevel] = useState<any>();
    const [typeScore, setTypeScore] = useState('');
    const [products, setProducts] = useState([]);
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { secondLevelFacets, loading } = useSelector((state: DefaultRootStateProps) => state.healthContent);

    const intl = useIntl();

    useEffect(() => {
        const idMerchant = new URLSearchParams(location.search).get('idMerchant');
        dispatch(getSecondLevelFacets(Number(idMerchant) ?? 0));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        setSecondLevel(secondLevelFacets);
    }, [secondLevelFacets]);
    useEffect(() => {
        if (typeScore === 'Good') {
            setProducts(secondLevel?.metricsGood);
        } else if (typeScore === 'Fair') {
            setProducts(secondLevel?.metricsFair);
        } else if (typeScore === 'Poor') {
            setProducts(secondLevel?.metricsPoor);
        } else {
            setProducts(secondLevel?.metricsGood);
        }
    }, [secondLevel?.metricsFair, secondLevel?.metricsGood, secondLevel?.metricsPoor, typeScore]);

    const sumaValores = () => {
        let suma = 0;
        suma =
            secondLevel?.resumeProducts[0]?.totalProducts +
            secondLevel?.resumeProducts[1]?.totalProducts +
            secondLevel?.resumeProducts[2]?.totalProducts;
        return suma;
    };

    return (
        <MainCard>
            <CardWrapper
                border={false}
                content={false}
                sx={{
                    mb: 2,
                    background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[50],
                    border: '1px solid',
                    borderColor: theme.palette.mode === 'dark' ? 'transparent' : theme.palette.grey[100]
                }}
            >
                <Box sx={{ p: 2.25 }}>
                    <Grid container direction="column">
                        <Grid item>
                            <Grid container alignItems="center">
                                <Grid item sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                                    <Link to="/loolu/admin/health-content/overall-score">
                                        <ArrowBackIcon fontSize="inherit" sx={{ transform: 'scale(2)', color: 'white' }} />
                                    </Link>
                                    <Typography sx={{ fontSize: '2.125rem', fontWeight: 500 }}>Facets</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item sx={{ mb: 1.25, mt: 2 }} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                            <Typography
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : theme.palette.secondary[200]
                                }}
                            >
                                Total
                            </Typography>
                            <DownloadReportsHealth dataToDownload={secondLevelFacets} />
                        </Grid>
                    </Grid>
                </Box>
            </CardWrapper>
            {loading ? (
                <Fade in={loading}>
                    <Grid container display={'flex'}>
                        <Grid item xs={4}>
                            <Skeleton variant="rectangular" height={140} sx={{ borderRadius: '10px' }} />
                            <Skeleton variant="rectangular" height={140} sx={{ borderRadius: '10px', mt: 2 }} />
                            <Skeleton variant="rectangular" height={140} sx={{ borderRadius: '10px', mt: 2 }} />
                        </Grid>
                        <Grid item xs={8}>
                            <Skeleton variant="rectangular" height={700} sx={{ borderRadius: '10px', ml: 2 }} />
                        </Grid>
                    </Grid>
                </Fade>
            ) : (
                <Grid container direction="row" xs={12}>
                    <Grid item xs={3.5}>
                        <Grid container direction="column" spacing={1} xs={12}>
                            {secondLevel?.resumeProducts
                                ?.toSorted((a: ResumenProducts, b: ResumenProducts) => a.metricConfigRangeId - b.metricConfigRangeId)
                                .map((resume: ResumenProducts) => (
                                    <Grid item xs={3}>
                                        <CardRatings resume={resume} sumaValores={sumaValores} setTypeScore={setTypeScore} />
                                    </Grid>
                                ))}
                        </Grid>
                    </Grid>
                    <Grid item xs={8.5}>
                        {products?.length === 0 ? (
                            <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <Typography variant="h3">{intl.formatMessage({ id: 'no_facets_to_show' })}</Typography>
                            </Box>
                        ) : (
                            <TableProducts products={products} typeReq="facets" />
                        )}
                    </Grid>
                </Grid>
            )}
        </MainCard>
    );
};

export default Facets;
